<!-- Structured as ion-header / ion-content / ion-footer, the layout Ionic guarantees for
a menu, rather than one ion-content holding a hand-rolled flex column. ion-content is
built to be a scroll container (its shadow .inner-scroll is absolutely positioned and
scrolls on its own); the previous shape instead asked a chain of height:100% and
flex/min-height rules to hand a bounded height down to an inner div, and when any link
in that chain didn't hold, the nav simply grew past the pane and nothing scrolled.
ion-header/ion-footer are sized to their content by .menu-inner's own column flex, so
the identity block and the language/legal links stay pinned either way. -->
<ion-menu contentId="main-content" type="overlay" side="start" (ionWillOpen)="onMenuOpen()">
  @if ({ auth: isAuthenticated$ | async, hasTenant: hasTenant$ | async, isAdmin: (isAdmin$ | async), badges: badgeCounts$ | async, marketing: showMarketingLinks$ | async, tenantName: tenantName$ | async, tenantLogoUrl: tenantLogoUrl$ | async, isPlatformAdmin: (isPlatformAdmin$ | async), portalReady: portalPagesReady$ | async }; as vm) {
    <!-- Pinned above the scroll area: which app you're in, and which tenant you're acting in. -->
    <ion-header class="sidepane-top ion-no-border">
      <div class="sidepane-header">
        <img src="assets/icon/favicon.ico" alt="App Logo" />
        <h1 color="light" class="app-title">Gastro Management</h1>
      </div>
      <!-- Tenant identity: logo (or initials) + name, directly under the app header.
      Non-interactive for a regular single-tenant user; a platform admin can tap it
      to open the tenant-switcher modal directly (same modal/service the profile
      menu uses, so the switch flow isn't implemented twice).
      The button semantics are bound rather than hard-coded: a plain div carrying a
      (click) is unreachable by keyboard and announces as nothing, but only the admin
      variant is actually actionable — so role/tabindex/aria only appear when the row
      really does something. Space is prevented from scrolling the pane behind it. -->
      @if (vm.tenantName) {
        <div class="tenant-row" [class.tenant-row--interactive]="vm.isPlatformAdmin"
          [attr.role]="vm.isPlatformAdmin ? 'button' : null" [attr.tabindex]="vm.isPlatformAdmin ? 0 : null"
          [attr.aria-haspopup]="vm.isPlatformAdmin ? 'dialog' : null"
          [attr.aria-label]="vm.isPlatformAdmin ? ('profileMenu.switchTenant' | translate) + ': ' + vm.tenantName : null"
          (click)="vm.isPlatformAdmin && tenantSwitcher.open()"
          (keydown.enter)="vm.isPlatformAdmin && tenantSwitcher.open()"
          (keydown.space)="vm.isPlatformAdmin && $event.preventDefault(); vm.isPlatformAdmin && tenantSwitcher.open()">
          <div class="tenant-mark" [class.tenant-mark--logo]="vm.tenantLogoUrl">
            @if (vm.tenantLogoUrl) {
              <img [src]="vm.tenantLogoUrl" alt="" />
            }
            @if (!vm.tenantLogoUrl) {
              <span>{{ tenantInitials(vm.tenantName) }}</span>
            }
          </div>
          <div class="tenant-info">
            <span class="tenant-name">{{ vm.tenantName }}</span>
            @if (vm.isPlatformAdmin) {
              <span class="tenant-sub">{{ 'profileMenu.switchTenant' | translate }}</span>
            }
          </div>
          @if (vm.isPlatformAdmin) {
            <ion-icon name="swap-vertical-outline" class="tenant-chevron"
            aria-hidden="true"></ion-icon>
          }
        </div>
      }
    </ion-header>
    <!-- The scroll container for the whole nav: Home, the marketing links and both
    accordions — section headers and page items alike — scroll together in here. -->
    <ion-content class="sidepane-wrapper">
      @if (!vm.auth) {
        <div class="login-button-container">
          <div class="login-info-box">
            <ion-icon name="lock-closed-outline"></ion-icon>
            <p>{{ 'menu.loginPrompt' | translate }}</p>
          </div>
          <button class="login-button" (click)="login()">
            <span>{{ 'menu.login' | translate }}</span>
          </button>
        </div>
      }
      <div class="accordion-content">
        <!-- Startseite: always visible, no accordion -->
        @for (u of homePages; track u) {
          <ion-menu-toggle auto-hide="true">
            <ion-item [routerLink]="u.url" routerDirection="root" lines="none" detail="false"
              routerLinkActive="selected">
              <div slot="start" class="nav-chip"><ion-icon [name]="u.icon"></ion-icon></div>
              <ion-label>{{ u.title | translate }}</ion-label>
              @if (badgeOf(vm.badges, u.url); as n) {
                <span slot="end" class="nav-badge">{{ n }}</span>
              }
            </ion-item>
          </ion-menu-toggle>
        }
        <!-- Fixed public pages (products/pricing/status/developer/contact): the shell
        header carries these as its own row above $bp-lg (see app.component.html
        .public-nav); below that breakpoint there's no room for it, so they list
        here instead, directly under Home as part of the same top-level group.
        ng-container (not a wrapper div) so each toggle stays a direct child of
        .accordion-content and picks up the same item/chip styling as Home. -->
        @if (vm.marketing) {
          @for (u of marketingPages; track u) {
            <ion-menu-toggle auto-hide="true" class="marketing-link"
              >
              @if (u.external) {
                <!-- Another site (Wiki-SPA): a plain link in a new tab, not a route. -->
                <ion-item [href]="u.url" target="_blank" rel="noopener" lines="none" detail="false">
                  <div slot="start" class="nav-chip"><ion-icon [name]="u.icon"></ion-icon></div>
                  <ion-label>{{ u.title | translate }}</ion-label>
                </ion-item>
              } @else {
                <ion-item [routerLink]="u.url" routerDirection="root" lines="none" detail="false"
                  routerLinkActive="selected">
                  <div slot="start" class="nav-chip"><ion-icon [name]="u.icon"></ion-icon></div>
                  <ion-label>{{ u.title | translate }}</ion-label>
                </ion-item>
              }
            </ion-menu-toggle>
          }
        }
        <ion-accordion-group expand="inset" value="portal" #accordionGroup>
          @if (vm.auth && vm.hasTenant === true) {
            <ion-accordion value="portal">
              <ion-item slot="header" lines="none">
                <ion-label>{{ 'menu.sectionPortal' | translate }}</ion-label>
              </ion-item>
              <div slot="content">
                @let portalPages = visiblePortalPages(vm.isAdmin ?? false);
                @if (portalPages.length === 0 && vm.portalReady && !vm.isAdmin) {
                  <!-- A member the administrator has not granted anything yet (a new invitation, for one). -->
                  <p class="menu-empty">{{ 'menu.noAreas' | translate }}</p>
                }
                @for (u of portalPages; track u) {
                  <ion-menu-toggle auto-hide="true"
                    >
                    <ion-item [routerLink]="u.url" routerDirection="root" lines="none" detail="false"
                      routerLinkActive="selected">
                      <div slot="start" class="nav-chip">
                        @if (u.iconSrc) {
                          <img [src]="u.iconSrc" alt="" class="nav-chip__mark" />
                        }
                        @if (!u.iconSrc) {
                          <ion-icon [name]="u.icon"></ion-icon>
                        }
                      </div>
                      <ion-label>{{ u.title | translate }}</ion-label>
                      @if (badgeOf(vm.badges, u.url); as n) {
                        <span slot="end" class="nav-badge">{{ n }}</span>
                      }
                    </ion-item>
                  </ion-menu-toggle>
                }
              </div>
            </ion-accordion>
          }
          @if (vm.auth && vm.isAdmin) {
            <ion-accordion value="administration">
              <ion-item slot="header" lines="none">
                <ion-label>{{ 'menu.sectionAdministration' | translate }}</ion-label>
              </ion-item>
              <div slot="content">
                @for (u of visibleAdminPages(vm.isAdmin); track u) {
                  <ion-menu-toggle auto-hide="true">
                    <ion-item [routerLink]="u.url" routerDirection="root" lines="none" detail="false"
                      routerLinkActive="selected">
                      <div slot="start" class="nav-chip"><ion-icon [name]="u.icon"></ion-icon></div>
                      <ion-label>{{ u.title | translate }}</ion-label>
                      @if (badgeOf(vm.badges, u.url); as n) {
                        <span slot="end" class="nav-badge">{{ n }}</span>
                      }
                    </ion-item>
                  </ion-menu-toggle>
                }
              </div>
            </ion-accordion>
          }
        </ion-accordion-group>
      </div>
    </ion-content>
    <!-- Pinned below the scroll area, so the legal links stay reachable however long the
    nav gets — the same reason the ScreenDeck menu keeps its legal row out of
    ion-content. -->
    <ion-footer class="sidepane-bottom ion-no-border">
      <div class="sidepane-footer">
        <!-- Language (visitors only; a signed-in user's language is the one chosen in My-SPA): one segmented pill instead of two loose buttons, so the selected
        language reads as a filled segment. The accent is applied here as *ink*,
        not as the brand fill — var(--ion-color-primary) lands near 2:1 as text on
        --surface-dark, which is why the active language used to be the harder of
        the two to read. See the --accent-ink note in theme/variables.scss. -->
        @if (!vm.auth) {
          <div class="footer-lang" role="group" [attr.aria-label]="'menu.language' | translate">
            <button type="button" class="footer-lang__btn" [class.is-active]="currentLang === 'de'"
              (click)="setLanguage('de')" [attr.aria-pressed]="currentLang === 'de'"
              [attr.aria-label]="'profil.language.de' | translate">
              <img class="footer-lang__flag" src="assets/icon/flags/de.svg" alt="" />
              <span>DE</span>
            </button>
            <button type="button" class="footer-lang__btn" [class.is-active]="currentLang === 'en'"
              (click)="setLanguage('en')" [attr.aria-pressed]="currentLang === 'en'"
              [attr.aria-label]="'profil.language.en' | translate">
              <img class="footer-lang__flag" src="assets/icon/flags/gb.svg" alt="" />
              <span>EN</span>
            </button>
          </div>
        }
        <!-- Legal + cookie settings: one row per entry rather than a single horizontal
        strip. Four labels never fit the drawer width in either language ("Terms &
        Conditions", "Cookie-Einstellungen"), so the strip overflowed the pane and
        broke labels mid-phrase. Rows also let the open legal page mark itself
        active, the same way the nav items above do. The three documents are real
        routes, so they are anchors (middle-click / open-in-new-tab work); cookie
        settings opens a modal and stays a button. -->
        <nav class="footer-legal" [attr.aria-label]="'home.footerLegal' | translate">
          <ion-menu-toggle auto-hide="false">
            <a class="footer-legal__item" [routerLink]="'/imprint'" routerLinkActive="is-active">
              <ion-icon name="document-text-outline" aria-hidden="true"></ion-icon>
              <span>{{ 'menu.impressum' | translate }}</span>
            </a>
          </ion-menu-toggle>
          <ion-menu-toggle auto-hide="false">
            <a class="footer-legal__item" [routerLink]="'/privacy'" routerLinkActive="is-active">
              <ion-icon name="lock-closed-outline" aria-hidden="true"></ion-icon>
              <span>{{ 'menu.datenschutz' | translate }}</span>
            </a>
          </ion-menu-toggle>
          <ion-menu-toggle auto-hide="false">
            <a class="footer-legal__item" [routerLink]="'/terms'" routerLinkActive="is-active">
              <ion-icon name="reader-outline" aria-hidden="true"></ion-icon>
              <span>{{ 'menu.agb' | translate }}</span>
            </a>
          </ion-menu-toggle>
          @if (wikiUrl) {
            <a class="footer-legal__item" [href]="wikiUrl" target="_blank" rel="noopener">
              <ion-icon name="book-outline" aria-hidden="true"></ion-icon>
              <span>{{ 'home.subnav.wiki' | translate }}</span>
            </a>
          }
          <button type="button" class="footer-legal__item" (click)="openCookieSettings()">
            <ion-icon name="shield-checkmark-outline" aria-hidden="true"></ion-icon>
            <span>{{ 'cookieConsent.settingsTitle' | translate }}</span>
          </button>
        </nav>
      </div>
    </ion-footer>
  }
</ion-menu>
